// Workbooks (design 02 3.1, 11.4): the list (a PC's sidebar, a phone's
// sheet), making a new one (비공개 unless "가족에게 공개" is chosen), and its
// settings: name, who sees it (the owner only), deleting (the owner; an
// administrator also a family one).
import { type FormEvent, useEffect, useState } from 'react'

import { errorText } from '../../api/client'
import { ErrorText, Field, Sheet } from '../../ui/common'
import { showToast } from '../../ui/toast'
import type { Id, TensorWorkbook } from '../api'
import { engine } from '../engine'
import { NAME_MAX } from '../csv'
import { userName } from '../model'
import { getState, useT } from '../store'
import { VisibilityBadge } from './common'

export function sortedWorkbooks(w: Record<number, TensorWorkbook>): TensorWorkbook[] {
  return Object.values(w).sort((a, b) => a.name.localeCompare(b.name, 'ko') || a.id - b.id)
}

/** "내가 만듦" or "엄마가 만듦" */
function ownerText(w: TensorWorkbook, me: Id): string {
  return w.owner_id === me ? '내가 만듦' : `${userName(getState(), w.owner_id)} 만듦`
}

export function WorkbookList({ onPick, onNew }: { onPick: (id: Id) => void; onNew: () => void }) {
  const workbooks = useT((s) => s.workbooks)
  const current = useT((s) => s.current)
  const me = useT((s) => s.me?.id ?? 0)
  const list = sortedWorkbooks(workbooks)
  return (
    <nav className="t-books" aria-label="통합 문서">
      <ul className="list">
        {list.map((w) => (
          <li key={w.id}>
            <button
              type="button"
              className="t-book"
              aria-current={w.id === current ? 'true' : undefined}
              onClick={() => onPick(w.id)}
            >
              <span className="t-book-name">
                {w.name}
                <VisibilityBadge workbook={w} />
              </span>
              <span className="muted small">{ownerText(w, me)}</span>
            </button>
          </li>
        ))}
      </ul>
      <button type="button" className="secondary" onClick={onNew}>
        새 통합 문서
      </button>
    </nav>
  )
}

export function NewWorkbookSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} onClose={onClose} title="새 통합 문서">
      {open && <NewWorkbookForm onDone={onClose} />}
    </Sheet>
  )
}

function NewWorkbookForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('')
  const [family, setFamily] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    const n = name.trim().normalize('NFC')
    if (n === '' || busy) return
    setBusy(true)
    setError(null)
    try {
      await engine.createWorkbook(n, family ? 'family' : 'private')
      onDone()
    } catch (err) {
      setError(errorText(err))
      setBusy(false)
    }
  }

  return (
    <form className="t-form" onSubmit={(e) => void submit(e)}>
      <Field label="이름" hint="예: 우리집 2026">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX} required />
      </Field>
      <label className="check">
        <input type="checkbox" checked={family} onChange={(e) => setFamily(e.target.checked)} />
        가족에게 공개
      </label>
      <p className="muted small">
        {family
          ? '가족 모두가 보고 고칠 수 있습니다. 공개 범위는 나중에 바꿀 수 있습니다.'
          : '나만 봅니다. 나중에 가족에게 공개할 수 있습니다.'}
      </p>
      <ErrorText text={error} />
      <button className="primary" type="submit" disabled={busy || name.trim() === ''}>
        만들기
      </button>
    </form>
  )
}

/** The open workbook's settings card. */
export function WorkbookSettings({ workbook }: { workbook: TensorWorkbook }) {
  const me = useT((s) => s.me)
  const [name, setName] = useState(workbook.name)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // shown at once; back as it was when the server says no
  const [family, setFamily] = useState(workbook.visibility === 'family')
  useEffect(() => setFamily(workbook.visibility === 'family'), [workbook.visibility])
  const [alerts, setAlerts] = useState(workbook.budget_alerts !== false)
  useEffect(() => setAlerts(workbook.budget_alerts !== false), [workbook.budget_alerts])
  const owner = me?.id === workbook.owner_id
  const canDelete = owner || (me?.admin === true && workbook.visibility === 'family')

  async function run(fn: () => Promise<void>): Promise<boolean> {
    setBusy(true)
    setError(null)
    try {
      await fn()
      return true
    } catch (e) {
      setError(errorText(e))
      return false
    } finally {
      setBusy(false)
    }
  }

  async function share(on: boolean) {
    setFamily(on)
    if (!(await run(() => engine.updateWorkbook(workbook.id, { visibility: on ? 'family' : 'private' }))))
      setFamily(workbook.visibility === 'family')
  }

  async function budgetAlerts(on: boolean) {
    setAlerts(on)
    if (!(await run(() => engine.updateWorkbook(workbook.id, { budget_alerts: on }))))
      setAlerts(workbook.budget_alerts !== false)
  }

  function rename(e: FormEvent) {
    e.preventDefault()
    const n = name.trim().normalize('NFC')
    if (n === '' || n === workbook.name) return
    void run(async () => {
      await engine.updateWorkbook(workbook.id, { name: n })
      showToast('이름을 바꿨습니다.')
    })
  }

  function remove() {
    if (!confirm(`"${workbook.name}"을(를) 지울까요? 안의 거래와 예산도 함께 지워집니다.`)) return
    void run(() => engine.deleteWorkbook(workbook.id))
  }

  return (
    <section className="card">
      <h3>통합 문서</h3>
      <form className="row" onSubmit={rename}>
        <Field label="통합 문서 이름">
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={NAME_MAX} required />
        </Field>
        <button className="secondary t-end" type="submit" disabled={busy || name.trim() === workbook.name}>
          이름 바꾸기
        </button>
      </form>
      <p className="muted small">
        <VisibilityBadge workbook={workbook} /> {ownerText(workbook, me?.id ?? 0)}
      </p>
      {owner ? (
        <label className="check">
          <input
            type="checkbox"
            checked={family}
            disabled={busy}
            onChange={(e) => void share(e.target.checked)}
          />
          가족에게 공개
        </label>
      ) : (
        <p className="muted small">공개 범위는 만든 사람만 바꿀 수 있습니다.</p>
      )}
      <label className="check">
        <input
          type="checkbox"
          checked={alerts}
          disabled={busy}
          onChange={(e) => void budgetAlerts(e.target.checked)}
        />
        예산 알림
      </label>
      <p className="muted small">
        이번 달을 포함한 최근 3개월 동안 한 달 한 분류의 지출이 예산을 넘으면, 이 통합 문서를 볼 수 있는 사람에게 메신저로 한 번 알립니다.
      </p>
      <ErrorText text={error} />
      {canDelete && (
        <button type="button" className="danger-btn" disabled={busy} onClick={remove}>
          통합 문서 지우기
        </button>
      )}
    </section>
  )
}
